/**
 * Demo step (wellness): shown right after StepProblem on /demo-benessere-posturale.
 * Hero preview of the 4 Wellness Recovery Suite™ pillars.
 */
import React from "react";
import {
  Activity, Heart, BarChart3, Target, Check, Crown, Stethoscope,
  Sparkles, ShieldCheck, Camera, Dumbbell, Smartphone, Bell,
} from "lucide-react";

const PILLARS = [
  {
    icon: Activity,
    badge: "BODY MAP",
    title: "Mappa corporea intelligente",
    sub: "24 zone cliniche front + back, color-coded. Tocca, valuta, salva.",
    grad: "from-cyan-500 to-blue-600",
    chips: ["Front + Back", "8 stati clinici", "Audit log"],
  },
  {
    icon: Heart,
    badge: "RECOVERY TIMELINE",
    title: "Timeline visuale del recupero",
    sub: "Prima visita, sedute, miglioramenti, ricadute — in una sola vista.",
    grad: "from-emerald-500 to-teal-500",
    chips: ["Eventi clinici", "Progress tracking", "PDF export-ready"],
  },
  {
    icon: Camera,
    badge: "SMART POSTURE",
    title: "Foto posturali before/after",
    sub: "Slider drag-and-drop premium su 4 tipi di assessment. AI Vision-ready.",
    grad: "from-amber-500 to-orange-500",
    chips: ["Multi-foto", "Slider premium", "AI Vision-ready"],
  },
  {
    icon: Dumbbell,
    badge: "SMART EXERCISE",
    title: "Routine domiciliari condivisibili",
    sub: "24 esercizi · 8 categorie · routine builder · link WhatsApp paziente.",
    grad: "from-violet-500 to-fuchsia-500",
    chips: ["24 esercizi", "Adherence live", "Link paziente"],
  },
  {
    icon: Smartphone,
    badge: "PATIENT PORTAL",
    title: "App mobile-first per il paziente",
    sub: "Magic-link · Recovery Score animato · heatmap stile GitHub · documenti.",
    grad: "from-teal-500 to-cyan-500",
    chips: ["Recovery Score", "Heatmap 12wk", "White-label"],
  },
  {
    icon: Bell,
    badge: "PUSH PWA",
    title: "Notifiche W3C standard",
    sub: "Nuova routine, promemoria sedute, messaggi dello studio. App chiusa o aperta.",
    grad: "from-rose-500 to-pink-500",
    chips: ["W3C nativo", "Deep-link portale", "Auto-cleanup"],
  },
  {
    icon: BarChart3,
    badge: "PAIN TRACKING",
    title: "Grafico evoluzione dolore",
    sub: "VAS 0-10 con sparkline elegante. Il paziente vede i progressi.",
    grad: "from-rose-600 to-orange-500",
    chips: ["VAS scale", "Pre/post seduta", "Storico illimitato"],
  },
  {
    icon: Target,
    badge: "PACCHETTI SEDUTE",
    title: "Sedute residue auto-tracking",
    sub: "Scarica una seduta con un click. Ricevuta PDF generata in automatico.",
    grad: "from-indigo-500 to-violet-500",
    chips: ["Tracking live", "Auto-billing", "Scadenze"],
  },
];

const WellnessSuiteStep = () => (
  <div className="space-y-5" data-testid="wellness-suite-step">
    <div className="text-center">
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-cyan-600 to-emerald-600 text-white text-[9px] font-bold uppercase tracking-[0.2em] shadow">
        <Crown className="w-2.5 h-2.5" /> Step 3/12 · Recovery Suite
      </span>
      <h2 className="text-2xl sm:text-3xl font-bold mt-3">
        Otto tecnologie cliniche.<br className="hidden sm:block" /> Solo per fisio · osteo · postura.
      </h2>
      <p className="text-slate-400 text-sm mt-2 max-w-xl mx-auto">
        Trasformano lo studio da semplice agenda a piattaforma clinica completa.
        Dalla mappa del dolore al Patient Portal mobile premium.
      </p>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {PILLARS.map((p, i) => (
        <div
          key={i}
          className="rounded-2xl border border-slate-700/50 bg-slate-900/50 backdrop-blur p-5 hover:border-cyan-400/50 transition-colors"
          data-testid={`wellness-suite-step-pillar-${i}`}
        >
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${p.grad} flex items-center justify-center shadow-lg mb-3`}>
            <p.icon className="w-4 h-4 text-white" />
          </div>
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-cyan-300/80">{p.badge}</p>
          <h3 className="text-base font-bold text-white mt-1">{p.title}</h3>
          <p className="text-xs text-slate-400 leading-relaxed mt-1.5">{p.sub}</p>
          <div className="flex flex-wrap gap-1 mt-3">
            {p.chips.map(c => (
              <span key={c} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                {c}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
      Tutto incluso nel piano <strong className="text-white">PRO</strong>. GDPR · Audit log · Backup automatico.
    </div>
  </div>
);

export default WellnessSuiteStep;
